/**
 * ReportPreview Component
 * Shows a preview of the medical report with selected data
 */

import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import "moment/locale/fr";
moment.locale("fr");
import { 
  Calendar, 
  Clock, 
  FileText, 
  Pill, 
  Activity,
  CheckCircle,
  File,
  Stethoscope,
  Image,
  FileDown,
} from "lucide-react";
import { IMAGE_URL } from "../../app_url";
import CoverInfoEditor from "./CoverInfoEditor";
import PdfPageRenderer from "./PdfPageRenderer";

const ReportPreview = forwardRef(({
  patientInfo,
  appointments,
  selectedAppointments,
  selectedFields,
  appointmentFieldOverrides,
  doctorInfo,
  coverFieldVisibility,
  onEditCoverField,
  showHeader,
  showGeneratedDate,
}, ref) => {
  
  const filteredAppointments = useMemo(() => {
    return appointments?.filter(apt => selectedAppointments[apt.id]) || [];
  }, [appointments, selectedAppointments]);

  const getFieldsForAppointment = (appointmentId, category) => {
    return appointmentFieldOverrides?.[appointmentId]?.[category] || selectedFields[category] || [];
  };

  const hasFieldSelected = (appointmentId, category, field) => {
    const fields = getFieldsForAppointment(appointmentId, category);
    return fields.includes(field);
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Oui" : "Non";
    return value;
  };

  const renderHTML = (html) => {
    if (!html) return null;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // Check if a document is an image based on type or name
  const isImageDocument = (doc) => {
    const docType = doc?.file_type || doc?.type || doc?.mime || '';
    const docName = doc?.name || doc?.file_name || doc?.original_name || '';
    return docType.includes('image') || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(docName);
  };

  // Check if a document is a PDF
  const isPdfDocument = (doc) => {
    const docType = doc?.file_type || doc?.type || doc?.mime || '';
    const docName = doc?.name || doc?.file_name || doc?.original_name || '';
    return docType.includes('pdf') || /\.pdf$/i.test(docName);
  };

  // Get the accessible URL for a document
  const getDocUrl = (doc) => {
    const raw = doc?.signed_url || doc?.url || doc?.file_path || doc?.path || doc?.file || '';
    if (!raw) return '';
    return raw.startsWith('http') ? raw : (IMAGE_URL || '') + raw;
  };

  return (
    <div className="report-preview" ref={ref}>
      {/* ===== COVER PAGE: Doctor, Patient & Medical Info ===== */}
      <CoverInfoEditor
        variant="preview"
        doctorInfo={doctorInfo}
        patientInfo={patientInfo}
        coverFieldVisibility={coverFieldVisibility}
        onEditCoverField={onEditCoverField}
        showHeader={showHeader}
        showGeneratedDate={showGeneratedDate}
      />

      {/* Page break after cover page — consultations start on page 2 */}
      <div className="cover-page-break"></div>

      <div className="report-preview__content">
        {filteredAppointments.length === 0 ? (
          <div className="no-appointments">
            <Calendar size={48} />
            <p>Aucune consultation sélectionnée pour l'export</p>
          </div>
        ) : (
          filteredAppointments.map((appointment) => (
            <div key={appointment.id} className="appointment-section">
              <div className="appointment-section__header">
                <div className="appointment-date">
                  <Calendar size={16} />
                  <span>
                    {moment(appointment.appointment_date).format("DD MMMM YYYY")}
                  </span>
                  {hasFieldSelected(appointment.id, "general", "appointment_time") && (
                    <>
                      <Clock size={14} />
                      <span>
                        {moment(appointment.appointment_date).format("HH:mm")}
                      </span>
                    </>
                  )}
                </div>
                {hasFieldSelected(appointment.id, "general", "appointment_type") && 
                  appointment.appointment_type && (
                  <span className="appointment-type-badge">
                    {appointment.appointment_type}
                  </span>
                )}
              </div>

              {/* Observations Section */}
              {appointment.observations?.[0] && (
                <div className="section-block observations-block">
                  <h4>
                    <Stethoscope size={14} />
                    Observations
                  </h4>
                  
                  {hasFieldSelected(appointment.id, "observations", "medical_condition_title") &&
                    appointment.observations[0].medical_condition_title && (
                    <div className="field-row">
                      <span className="field-label">Pathologie :</span>
                      <span className="field-value">
                        {appointment.observations[0].medical_condition_title}
                      </span>
                    </div>
                  )}

                  {hasFieldSelected(appointment.id, "observations", "examination") &&
                    appointment.observations[0].examination && (
                    <div className="field-row field-row--full">
                      <span className="field-label">Examen :</span>
                      <div className="field-value html-content">
                        {renderHTML(appointment.observations[0].examination)}
                      </div>
                    </div>
                  )}

                  {hasFieldSelected(appointment.id, "observations", "other_notes") &&
                    appointment.observations[0].other_notes && (
                    <div className="field-row field-row--full">
                      <span className="field-label">Notes :</span>
                      <div className="field-value html-content">
                        {renderHTML(appointment.observations[0].other_notes)}
                      </div>
                    </div>
                  )}

                  {hasFieldSelected(appointment.id, "observations", "reminder_note") &&
                    appointment.observations[0].reminder_note && (
                    <div className="field-row field-row--full">
                      <span className="field-label">Rappel :</span>
                      <div className="field-value html-content">
                        {renderHTML(appointment.observations[0].reminder_note)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Prescriptions Section */}
              {appointment.prescription_content?.length > 0 &&
                getFieldsForAppointment(appointment.id, "prescriptions").length > 0 && (
                <div className="section-block prescriptions-block">
                  <h4>
                    <Pill size={14} />
                    Ordonnances ({appointment.prescription_content.length})
                  </h4>
                  
                  {appointment.prescription_content.map((prescription, idx) => (
                    <div key={idx} className="prescription-item">
                      {hasFieldSelected(appointment.id, "prescriptions", "template_name") && (
                        <div className="prescription-header">
                          <FileText size={12} />
                          <span>{prescription.template_name || `Ordonnance ${idx + 1}`}</span>
                          {hasFieldSelected(appointment.id, "prescriptions", "prescription_date") &&
                            prescription.prescription_date && (
                            <span className="prescription-date">
                              {moment(prescription.prescription_date).format("DD/MM/YYYY")}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {hasFieldSelected(appointment.id, "prescriptions", "prescription_body") &&
                        prescription.prescription_body && (
                        <div className="prescription-content">
                          {renderHTML(prescription.prescription_body)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Care Protocol Section */}
              {appointment.careSteps?.length > 0 &&
                getFieldsForAppointment(appointment.id, "careSteps").length > 0 && (
                <div className="section-block care-steps-block">
                  <h4>
                    <CheckCircle size={14} />
                    Protocole de soins
                  </h4>
                  
                  <div className="care-steps-list">
                    {appointment.careSteps.map((step, idx) => (
                      <div 
                        key={idx} 
                        className={`care-step ${step.is_done ? "care-step--done" : ""}`}
                      >
                        {hasFieldSelected(appointment.id, "careSteps", "is_done") && (
                          <span className="step-status">
                            {step.is_done ? "✓" : "○"}
                          </span>
                        )}
                        {hasFieldSelected(appointment.id, "careSteps", "service") && (
                          <span className="step-service">
                            {step.service || step.label || "Étape de soin"}
                          </span>
                        )}
                        {hasFieldSelected(appointment.id, "careSteps", "comment") &&
                          step.comment && (
                          <span className="step-comment">{step.comment}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Charts Section */}
              {appointment.charts?.length > 0 &&
                getFieldsForAppointment(appointment.id, "charts").length > 0 && (
                <div className="section-block charts-block">
                  <h4>
                    <Activity size={14} />
                    Mesures
                  </h4>
                  
                  <div className="charts-grid">
                    {appointment.charts.map((chart, idx) => (
                      <div key={idx} className="chart-item">
                        {hasFieldSelected(appointment.id, "charts", "property") && (
                          <span className="chart-property">
                            {chart.property || chart.parametre || chart.nom}
                          </span>
                        )}
                        {hasFieldSelected(appointment.id, "charts", "value") && (
                          <span className="chart-value">
                            {chart.value || chart.valeur || chart.poids}
                          </span>
                        )}
                        {hasFieldSelected(appointment.id, "charts", "measured_at") &&
                          chart.measured_at && (
                          <span className="chart-date">
                            {moment(chart.measured_at).format("DD/MM/YYYY")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {appointment.documents?.length > 0 &&
                getFieldsForAppointment(appointment.id, "documents").length > 0 && (
                <div className="section-block documents-block">
                  <h4>
                    <File size={14} />
                    Documents ({appointment.documents.length})
                  </h4>
                  
                  {/* PDF documents embedded inline */}
                  {appointment.documents.filter(doc => isPdfDocument(doc) && getDocUrl(doc)).length > 0 && (
                    <div className="documents-pdfs">
                      {appointment.documents
                        .filter(doc => isPdfDocument(doc) && getDocUrl(doc))
                        .map((doc, idx) => (
                          <div key={`pdf-${idx}`} className="document-pdf-item">
                            <div className="document-pdf-header">
                              <FileDown size={14} />
                              <span className="document-pdf-name">
                                {doc.name || doc.file_name || doc.original_name || 'Document PDF'}
                              </span>
                              {doc.created_at && (
                                <span className="document-pdf-date">
                                  {moment(doc.created_at).format("DD/MM/YYYY")}
                                </span>
                              )}
                            </div>
                            <PdfPageRenderer
                              src={getDocUrl(doc)}
                              className="document-pdf-embed"
                              scale={1.5}
                            />
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Image documents rendered inline */}
                  {appointment.documents.filter(doc => isImageDocument(doc) && getDocUrl(doc)).length > 0 && (
                    <div className="documents-images">
                      {appointment.documents
                        .filter(doc => isImageDocument(doc) && getDocUrl(doc))
                        .map((doc, idx) => (
                          <div key={`img-${idx}`} className="document-image-item">
                            <img
                              src={getDocUrl(doc)}
                              alt={doc.name || doc.file_name || doc.original_name || 'Document'}
                              className="document-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                              }}
                            />
                            <div className="document-image-fallback" style={{ display: 'none' }}>
                              <Image size={24} />
                              <span>Image indisponible</span>
                            </div>
                            <span className="document-image-caption">
                              {doc.name || doc.file_name || doc.original_name || 'Image'}
                              {doc.created_at && (
                                <small> — {moment(doc.created_at).format("DD/MM/YYYY")}</small>
                              )}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Other documents listed */}
                  {appointment.documents.filter(doc => !isImageDocument(doc) && !isPdfDocument(doc)).length > 0 && (
                    <div className="documents-list">
                      {appointment.documents
                        .filter(doc => !isImageDocument(doc) && !isPdfDocument(doc))
                        .map((doc, idx) => (
                          <div key={idx} className="document-item">
                            <File size={12} />
                            {hasFieldSelected(appointment.id, "documents", "name") && (
                              <span className="doc-name">
                                {doc.name || doc.file_name || doc.original_name}
                              </span>
                            )}
                            {hasFieldSelected(appointment.id, "documents", "file_type") && (
                              <span className="doc-type">
                                {doc.file_type || doc.type}
                              </span>
                            )}
                            {hasFieldSelected(appointment.id, "documents", "created_at") &&
                              doc.created_at && (
                              <span className="doc-date">
                                {moment(doc.created_at).format("DD/MM/YYYY")}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showGeneratedDate && (
        <div className="report-preview__footer">
          <p>
            Ce rapport a été généré depuis le système Doctolik.
          </p>
          <p className="confidential">
            CONFIDENTIEL - Ce document contient des informations médicales personnelles.
          </p>
        </div>
      )}
    </div>
  );
});

ReportPreview.displayName = "ReportPreview";

ReportPreview.propTypes = {
  patientInfo: PropTypes.object,
  appointments: PropTypes.array.isRequired,
  selectedAppointments: PropTypes.object.isRequired,
  selectedFields: PropTypes.object.isRequired,
  appointmentFieldOverrides: PropTypes.object,
  doctorInfo: PropTypes.object,
  coverFieldVisibility: PropTypes.object,
  onEditCoverField: PropTypes.func,
  showHeader: PropTypes.bool,
  showGeneratedDate: PropTypes.bool,
};

ReportPreview.defaultProps = {
  patientInfo: {},
  appointmentFieldOverrides: {},
  doctorInfo: {},
  coverFieldVisibility: null,
  onEditCoverField: null,
  showHeader: true,
  showGeneratedDate: true,
};

export default ReportPreview;
